import { useEffect, useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Card, Typography, Box, Select, MenuItem, FormControl } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { RiAddFill, RiEditLine } from "react-icons/ri";
import { IoRocketSharp, IoArrowUp, IoArrowDown, IoEyeSharp } from "react-icons/io5";
import { FaInstagram, FaTelegram, FaDiscord, FaTiktok, FaWhatsapp, FaEllipsisH } from 'react-icons/fa';
import DashboardLayout from "defaultComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "defaultComponents/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from 'components/VuiTypography';
import { CustomButton } from 'components/ButtonCustom/style';
import Modal from "components/Modal/Modal";
import TokenCounter from "components/TokenCounter/TokenCounter";
import calendarIcon from 'assets/icons/community-calendar.svg';
import medalIcon from 'assets/icons/medal.svg';
import placeholderImg from "assets/placeholder.png";
import * as S from './style';
import * as T from "components/Toast/style";
import showToast from "components/Toast/Toast";
import LineChart from "defaultComponents/Charts/LineCharts/LineChart";
import { getCommunitiesByUser, getCommunityFullCalendar } from 'utils/requests/communities';
import { TokenContext } from 'context/TokenContext';

const DashboardCard = ({ 
  icon, 
  title, 
  progressText, 
  progressLabel, 
  buttonText, 
  onButtonClick 
}) => {
  return (
    <S.DashboardCard>
      <S.CardContentBox>
        <S.IconContainer sx={{ height: "65px", width: "65px" }}>
          {typeof icon === 'string' ? (
            <img src={icon} style={{ width: "45px", height: "45px" }} />
          ) : (
            icon
          )}
        </S.IconContainer>
        <S.CardInfoBox>
          <Typography variant="body2" color="#FFFFFF">
            {title}
          </Typography>
          <S.CardProgressBox>
            {progressLabel ? (
              <>
                <S.CardProgressText>
                  {progressText}
                </S.CardProgressText>
                <S.CardProgressLabel>
                  {progressLabel}
                </S.CardProgressLabel>
              </>
            ) : (
              <S.CardModuleText>
                {progressText}
              </S.CardModuleText>
            )}
          </S.CardProgressBox>
        </S.CardInfoBox>
      </S.CardContentBox>
      <S.CardButtonBox>
        <CustomButton onClick={onButtonClick}>
          <S.CardButtonText>
            {buttonText}
          </S.CardButtonText>
        </CustomButton>
      </S.CardButtonBox>
    </S.DashboardCard>
  );
};

const CommunityDashboard = () => {
  const navigate = useNavigate();
  const { decodedToken } = useContext(TokenContext);

  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [parsedData, setParsedData] = useState({});
  const [openEditMembersModal, setOpenEditMembersModal] = useState(false);
  const [editableMembersData, setEditableMembersData] = useState({
    members: 0,
    previousMembers: 0,
    change: 0,
    isPositive: true,
    lastBaselineUpdate: new Date().toISOString(),
    baselinePeriodDays: 7,
    dailyHistory: {}
  });
  const [originalMembersData, setOriginalMembersData] = useState({});
  const [chartData, setChartData] = useState([
    {
      name: "Membros",
      data: Array(new Date().getDate()).fill(0)
    }
  ]);
  const [chartOptions, setChartOptions] = useState({
    chart: {
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      theme: "dark",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
    },
    xaxis: {
      type: "datetime",
      categories: Array.from({length: new Date().getDate()}, (_, i) => i + 1),
      labels: {
        style: {
          colors: "#c8cfca",
          fontSize: "10px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return Math.round(val);
        },
        style: {
          colors: "#c8cfca",
          fontSize: "10px",
        },
      },
    },
    legend: {
      show: false,
    },
    grid: {
      strokeDashArray: 5,
      borderColor: "#56577A",
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 0.8,
        opacityTo: 0,
        stops: [],
      },
      colors: ["#FC6009"],
    },
    colors: ["#FC6009"],
  });

  const influencerList = [
    {
      influencer: {
        name: "João da Silva",
        avatar: "https://randomuser.me/api/portraits/men/1.jpg",
      },
      interactions: 456,
      weeklyChange: {
        value: 57,
        isPositive: true,
      },
    },
    {
      influencer: {
        name: "Ricardo Oliveira",
        avatar: "https://randomuser.me/api/portraits/men/2.jpg",
      },
      interactions: 219,
      weeklyChange: {
        value: 0,
        isPositive: null,
      },
    },
    {
      influencer: {
        name: "Gabriel Camargo",
        avatar: "https://randomuser.me/api/portraits/men/3.jpg",
      },
      interactions: 132,
      weeklyChange: {
        value: 0,
        isPositive: null,
      },
    },
    {
      influencer: {
        name: "Felipe Santos",
        avatar: "https://randomuser.me/api/portraits/men/4.jpg",
      },
      interactions: 39,
      weeklyChange: {
        value: 12,
        isPositive: false,
      },
    },
    {
      influencer: {
        name: "Fátima Bertolli",
        avatar: "https://randomuser.me/api/portraits/women/1.jpg",
      },
      interactions: 23,
      weeklyChange: {
        value: 15,
        isPositive: true,
      },
    },
  ];

  const fetchCommunities = async () => {
    try {
      if (!decodedToken?.id) return;
      const data = await getCommunitiesByUser(decodedToken.id);
      setCommunities(data);
      if (data.length > 0) {
        setSelectedCommunity(data[0]);
      }
    } catch (error) {
      if (error.status !== 404) {
        showToast("Erro ao buscar comunidades.", "error");
      }
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  useEffect(() => {
    const loadCommunityData = () => {
      try {
        const savedData = localStorage.getItem('communityPlan');
        if (savedData) {
          const data = JSON.parse(savedData);
          if (data && typeof data === 'object') {
            if (data.membersData) {
              const completeData = {
                members: data.membersData.members || 0,
                previousMembers: data.membersData.previousMembers || 0,
                change: data.membersData.change || 0,
                isPositive: data.membersData.isPositive !== undefined ? data.membersData.isPositive : true,
                lastBaselineUpdate: data.membersData.lastBaselineUpdate || new Date().toISOString(),
                baselinePeriodDays: data.membersData.baselinePeriodDays || 7,
                dailyHistory: data.membersData.dailyHistory || {}
              };
              
              const updatedData = checkAndUpdateBaseline(completeData);
              setEditableMembersData(updatedData);
              
              if (updatedData.lastBaselineUpdate !== completeData.lastBaselineUpdate) {
                data.membersData = updatedData;
                localStorage.setItem('communityPlan', JSON.stringify(data));
                showToast("Baseline semanal atualizado! Nova referência: " + updatedData.previousMembers + " membros", "warning");
              }
              
              if (updatedData.members > 0 && Object.keys(updatedData.dailyHistory || {}).length === 0) {
                const today = new Date().getDate();
                updatedData.dailyHistory = { [today]: updatedData.members };
                
                data.membersData = updatedData;
                localStorage.setItem('communityPlan', JSON.stringify(data));
              }
              
              updateChartData(updatedData);
            }
          } else {
            console.warn("Dados do localStorage têm formato inválido");
            setParsedData({
              name: "Comunidade Padrão",
              purpose: "Descrição não disponível"
            });
          }
        } else {
          setEditableMembersData({
            members: 0,
            previousMembers: 0,
            change: 0,
            isPositive: true,
            lastBaselineUpdate: new Date().toISOString(),
            baselinePeriodDays: 7,
            dailyHistory: {}
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados da comunidade:", error);
      }
    };

    loadCommunityData();
  }, []);

  useEffect(() => {
    updateChartData(editableMembersData);
  }, [editableMembersData.members]);

  const [calendarProgress, setCalendarProgress] = useState({ completed: 0, total: 0 });

  useEffect(() => {
    if (selectedCommunity && selectedCommunity.id) {
      loadCalendarProgress();
    }
  }, [selectedCommunity]);

  const memoizedInfluencerList = useMemo(() => influencerList, []);

  const RankingItem = useMemo(() => ({ influencer, index }) => (
    <VuiBox key={`${influencer.influencer.name}-${index}`}>
      <S.RankingItemBox>
        <S.RankingGrid container gap='10px' alignItems='center'>
          <p>{index + 1}.</p>
          <S.RankingPhoto isFirst={index === 0}>
            <div className='photoContainer'>
              <img 
                src={influencer?.influencer?.avatar || placeholderImg} 
                width='100%' 
                height='100%' 
                draggable='false'
                alt={`Avatar de ${influencer?.influencer?.name}`}
              />
            </div>
            {index === 0 && <p>👑</p>}
          </S.RankingPhoto>
          <VuiBox display="flex" flexDirection="column">
            <VuiBox display="flex" alignItems="center" gap={0.5}>
              <S.RankingName>
                {influencer?.influencer?.name ?? ''}
              </S.RankingName>
              {influencer?.weeklyChange?.isPositive === true && (
                <IoArrowUp color="#65EFAD" size="18px" />
              )}
              {influencer?.weeklyChange?.isPositive === false && (
                <IoArrowDown color="#D7007B" size="18px" />
              )}
            </VuiBox>
            <VuiBox display="flex" alignItems="center" gap={0.5}>
              <S.RankingInteractions>
                {influencer?.interactions}
              </S.RankingInteractions>
              <S.RankingInteractionsLabel>
                Interações
              </S.RankingInteractionsLabel>
              {influencer?.weeklyChange?.value > 0 && (
                <S.RankingChange isPositive={influencer?.weeklyChange?.isPositive}>
                  ({influencer?.weeklyChange?.isPositive ? '+' : '-'}{influencer?.weeklyChange?.value} essa semana)
                </S.RankingChange>
              )}
              {influencer?.weeklyChange?.value === 0 && (
                <S.RankingChange isPositive={false}>
                  (0 essa semana)
                </S.RankingChange>
              )}
            </VuiBox>
          </VuiBox>
        </S.RankingGrid>
      </S.RankingItemBox>
    </VuiBox>
  ), [placeholderImg]);

  const handleCreateCommunity = () => {
    navigate('/comunidade/criar');
  };

  const handleViewCommunity = () => {
    navigate(`/comunidade/agente/${selectedCommunity.id}`);
  };

  const handleNavigateToCalendar = () => {
    navigate(`/comunidade/agente/${selectedCommunity.id}?tab=calendar`);
  };

  const handleNavigateToScripts = () => {
    navigate(`/comunidade/agente/${selectedCommunity.id}?tab=routines`);
  };

  const handleNavigateToMedals = () => {
    navigate(`/comunidade/agente/${selectedCommunity.id}?tab=medals`);
  };

  const handleOpenEditMembersModal = () => {
    setOriginalMembersData({ ...editableMembersData });
    setOpenEditMembersModal(true);
  };

  const handleCloseEditMembersModal = () => {
    setEditableMembersData({ ...originalMembersData });
    setOpenEditMembersModal(false);
  };

  const handleSaveMembersData = () => {
    try {
      const currentMembers = editableMembersData.members;
      const previousMembers = editableMembersData.previousMembers;
      
      const today = new Date().getDate();
      
      const updatedDailyHistory = {
        ...editableMembersData.dailyHistory,
        [today]: currentMembers
      };
      
      const isFirstRecord = previousMembers === 0 && currentMembers > 0;
      
      let updatedMembersData;
      
      if (isFirstRecord) {
        updatedMembersData = {
          members: currentMembers,
          previousMembers: currentMembers,
          change: 0,
          isPositive: true,
          lastBaselineUpdate: new Date().toISOString(),
          baselinePeriodDays: 7,
          dailyHistory: updatedDailyHistory
        };
      } else {
        const change = currentMembers - previousMembers;
        const isPositive = change >= 0;
        
        updatedMembersData = {
          members: currentMembers,
          previousMembers: previousMembers,
          change: Math.abs(change),
          isPositive: isPositive,
          lastBaselineUpdate: editableMembersData.lastBaselineUpdate || new Date().toISOString(),
          baselinePeriodDays: editableMembersData.baselinePeriodDays || 7,
          dailyHistory: updatedDailyHistory
        };
      }

      setEditableMembersData(updatedMembersData);
      
      updateChartData(updatedMembersData);

      const savedData = localStorage.getItem('communityPlan');
      if (savedData) {
        const data = JSON.parse(savedData);
        data.membersData = updatedMembersData;
        localStorage.setItem('communityPlan', JSON.stringify(data));
      } else {
        const newData = {
          membersData: updatedMembersData,
          name: "Nova Comunidade",
          purpose: "Configure sua comunidade"
        };
        localStorage.setItem('communityPlan', JSON.stringify(newData));
      }
      
      setOpenEditMembersModal(false);
      
      setTimeout(() => {
        if (isFirstRecord) {
          showToast(`Primeiro registro salvo! Você está iniciando sua comunidade com ${currentMembers} membros`, "success");
        } else {
          const change = currentMembers - previousMembers;
          const isPositive = change >= 0;
          showToast(`Membros atualizados! Variação: ${isPositive ? '+' : '-'}${Math.abs(change)} membros`, "success");
        }
      }, 200);
      
    } catch (error) {
      console.error("Erro ao salvar dados de membros:", error);
      showToast("Erro ao salvar dados de membros", "error");
    }
  };

  const handleMembersDataChange = (field, value) => {
    setEditableMembersData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateChartData = (membersData) => {
    const generateChartData = (membersData) => {
      const today = new Date().getDate();
      const dailyHistory = membersData.dailyHistory || {};
      const currentMembers = membersData.members || 0;
      
      let lastKnownValue = 0;
      const data = Array.from({length: today}, (_, i) => {
        const day = i + 1;
        
        if (dailyHistory[day] !== undefined) {
          lastKnownValue = dailyHistory[day];
          return dailyHistory[day];
        }
        
        if (day === today && currentMembers > 0) {
          lastKnownValue = currentMembers;
          return currentMembers;
        }
        
        return lastKnownValue;
      });

      return [{
        name: "Membros",
        data: data
      }];
    };

    const newChartData = generateChartData(membersData);
    setChartData(newChartData);

    const today = new Date().getDate();
    const maxValue = Math.max(...newChartData[0].data, membersData.members || 0);
    const chartMax = maxValue > 0 ? Math.ceil(maxValue * 1.2) : 100;
    
    setChartOptions(prev => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        categories: Array.from({length: today}, (_, i) => i + 1),
      },
      yaxis: {
        ...prev.yaxis,
        min: 0,
        max: chartMax,
      }
    }));
  };

  const getBaselineVariation = () => {
    const currentMembers = editableMembersData.members || 0;
    const previousMembers = editableMembersData.previousMembers || 0;
    
    if (currentMembers === 0 && previousMembers === 0) {
      return { value: 0, isPositive: true, text: "(0)" };
    }
    
    const variation = currentMembers - previousMembers;
    const isPositive = variation >= 0;
    
    return {
      value: Math.abs(variation),
      isPositive,
      text: isPositive ? `(+${Math.abs(variation)})` : `(-${Math.abs(variation)})`
    };
  };

  const getMonthlyVariation = () => {
    const currentMembers = editableMembersData.members || 0;
    const dailyHistory = editableMembersData.dailyHistory || {};
    
    if (currentMembers === 0) {
      return { value: 0, isPositive: true, text: "(0)" };
    }
    
    const daysWithData = Object.keys(dailyHistory)
      .map(day => parseInt(day))
      .filter(day => !isNaN(day))
      .sort((a, b) => a - b);
    
    if (daysWithData.length === 0) {
      return { value: 0, isPositive: true, text: "(0)" };
    }
    
    if (daysWithData.length === 1) {
      const todayValue = currentMembers;
      return {
        value: todayValue,
        isPositive: true,
        text: `(+${todayValue})`
      };
    }
    
    const firstDayWithData = daysWithData[0];
    const firstDayValue = dailyHistory[firstDayWithData] || 0;
    const variation = currentMembers - firstDayValue;
    const isPositive = variation >= 0;
    
    return {
      value: Math.abs(variation),
      isPositive,
      text: isPositive ? `(+${Math.abs(variation)})` : `(-${Math.abs(variation)})`
    };
  };

  const checkAndUpdateBaseline = (currentData) => {
    const now = new Date();
    const lastUpdate = new Date(currentData.lastBaselineUpdate);
    
    const getLastSunday = (date) => {
      const d = new Date(date.getTime());
      const day = d.getDay();
      const diff = d.getDate() - day;
      const lastSunday = new Date(d.getFullYear(), d.getMonth(), diff);
      lastSunday.setHours(0, 0, 0, 0);
      return lastSunday;
    };
    
    const lastSunday = getLastSunday(now);
    const lastUpdateDate = new Date(lastUpdate.getTime());
    lastUpdateDate.setHours(0, 0, 0, 0);
    
    if (lastUpdateDate.getTime() < lastSunday.getTime()) {
      return {
        ...currentData,
        previousMembers: currentData.members,
        lastBaselineUpdate: new Date().toISOString(),
        change: 0,
        isPositive: true
      };
    }

    return currentData;
  };

  const loadCalendarProgress = async () => {
    if (!selectedCommunity) return;
    try {
      const calendarData = await getCommunityFullCalendar(selectedCommunity.id);
      
      if (!calendarData?.calendars?.length) {
        setCalendarProgress({
          total: 0,
          completed: 0,
          percentage: 0
        });
        return;
      }
  
      const calendar = calendarData.calendars[0];
      if (!calendar.weeks?.length) {
        setCalendarProgress({
          total: 0,
          completed: 0,
          percentage: 0
        });
        return;
      }
  
      let total = 0;
      let completed = 0;
      calendar.weeks.forEach(week => {
        if(week.posts?.length) {
          total += week.posts.length;
          completed += week.posts.filter(post => {
            const scriptData = typeof post.script_data === 'string' ? JSON.parse(post.script_data) : post.script_data;
            return scriptData?.completed;
          }).length;
        }
      });
  
      setCalendarProgress({
        total: total,
        completed: completed,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0
      });
    } catch (error) {
      if (error.status !== 404) {
        showToast("Erro ao carregar o progresso do calendário.", "error");
      }
    }
  };

  const getCurrentWeekInfo = () => {
    if (!selectedCommunity || !calendarProgress) {
      return {
        week: 1,
        text: "Você está na 1ª semana do ciclo de 30 dias"
      };
    }
    
    const progressPercent = calendarProgress.percentage;
    
    let currentWeek = 1;
    let weekText = "";
    
    if (progressPercent >= 100) {
      weekText = "Parabéns! Você concluiu o seu calendário!";
    } else if (progressPercent >= 75) {
      currentWeek = 4;
      weekText = "Você está na 4ª semana do ciclo de 30 dias";
    } else if (progressPercent >= 50) {
      currentWeek = 3;
      weekText = "Você está na 3ª semana do ciclo de 30 dias";
    } else if (progressPercent >= 25) {
      currentWeek = 2;
      weekText = "Você está na 2ª semana do ciclo de 30 dias";
    } else {
      currentWeek = 1;
      weekText = "Você está na 1ª semana do ciclo de 30 dias";
    }
    
    return { week: currentWeek, text: weekText };
  };

  const getPlatformIcon = () => {
    let platform = parsedData?.formData?.platform;
    
    if (!platform && parsedData?.planDetails) {
      try {
        const planDetailsObject = typeof parsedData.planDetails === 'string' 
          ? JSON.parse(parsedData.planDetails)
          : parsedData.planDetails;
        platform = planDetailsObject?.platform;
      } catch (e) {
        console.warn("Erro ao processar platform dos planDetails:", e);
      }
    }
    
    if (!platform) {
      return <FaInstagram size={40} color="white" />;
    }
    
    const mainPlatform = Array.isArray(platform) ? platform[0] : platform;
    
    switch (mainPlatform?.toLowerCase()) {
      case 'instagram':
        return <FaInstagram size={40} color="white" />;
      case 'whatsapp':
        return <FaWhatsapp size={40} color="white" />;
      case 'telegram':
        return <FaTelegram size={40} color="white" />;
      case 'discord':
        return <FaDiscord size={40} color="white" />;
      case 'tiktok':
        return <FaTiktok size={40} color="white" />;
      case 'other':
      case 'outro':
        return <FaEllipsisH size={40} color="white" />;
      default:
        return <FaInstagram size={40} color="white" />;
    }
  };

  const getNextBaselineUpdateInfo = () => {
    if (!editableMembersData.lastBaselineUpdate) {
      return "Calculando próxima atualização...";
    }
    
    try {
      const now = new Date();
      
      const getNextSunday = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const daysUntilSunday = day === 0 ? 7 : 7 - day;
        return new Date(d.setDate(d.getDate() + daysUntilSunday));
      };
      
      const nextSunday = getNextSunday(now);
      const daysUntilSunday = Math.ceil((nextSunday - now) / (1000 * 60 * 60 * 24));
      
      if (now.getDay() === 0) {
        return "Baseline será atualizado na próxima edição (hoje é domingo!)";
      } else if (daysUntilSunday === 1) {
        return "Baseline será atualizado amanhã (domingo)";
      } else {
        return `Baseline será atualizado no próximo domingo (em ${daysUntilSunday} dias)`;
      }
    } catch (error) {
      console.error("Erro ao calcular próxima atualização:", error);
      return "Erro ao calcular próxima atualização";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} lg={12} xl={12}>
              <Card sx={{ height: "100%", py: "32px", padding: "25px 22px" }}>
                <VuiBox height="100%" display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-start">
                  <VuiBox flex={1} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                    <VuiBox flex={1} display="flex" flexDirection="column">
                      <FormControl variant="standard" sx={{ mb: 1, width: "fit-content" }}>
                        <Select
                          value={selectedCommunity?.id || ''}
                          onChange={e => {
                            const community = communities.find(comm => comm.id === e.target.value);
                            setSelectedCommunity(community);
                          }}
                          displayEmpty
                          IconComponent={KeyboardArrowDown}
                          sx={{
                            fontSize: "36px",
                            color: "#FFFFFF",
                            opacity: "0.9",
                            fontWeight: "regular",
                            "& .MuiSelect-select": {
                              paddingTop: 0,
                              paddingBottom: 0,
                              paddingLeft: 0,
                              paddingRight: "62px !important",
                              display: "flex",
                              alignItems: "center"
                            },
                            "& .MuiSelect-icon": {
                              color: "#FFFFFF",
                              opacity: 0.7,
                              fontSize: "28px",
                              right: "4px",
                              marginLeft: "10px",
                              marginTop: "5px"
                            },
                            "&:before": {
                              display: "none"
                            },
                            "&:after": {
                              display: "none"
                            },
                            "&:hover:not(.Mui-disabled):before": {
                              display: "none"
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: 'rgba(0, 0, 0, 0.9)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                maxHeight: '300px'
                              }
                            }
                          }}
                        >
                          {communities.map(community => {
                            let name = '';
                            if (community.plan_details) {
                              try {
                                const plan = typeof community.plan_details === 'string' ? JSON.parse(community.plan_details) : community.plan_details;
                                name = plan?.name || community.name || 'Sem nome';
                              } catch {
                                name = community.name || 'Sem nome';
                              }
                            } else {
                              name = community.name || 'Sem nome';
                            }
                            return (
                              <MenuItem key={community.id} value={community.id} sx={{
                                color: '#FFFFFF',
                                fontSize: "18px",
                                '&:hover': {
                                  backgroundColor: 'rgba(252, 101, 8, 0.1)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(252, 101, 8, 0.2)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(252, 101, 8, 0.3)'
                                  }
                                }
                              }}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <VuiTypography color="text" variant="button" fontWeight="regular" sx={{ fontSize: "14px", color: "#E7E3FC99", mb: 3 }}>
                        {(() => {
                          if (!selectedCommunity) return 'Descrição da comunidade';
                          let purpose = selectedCommunity.purpose;
                          if (selectedCommunity.plan_details) {
                            try {
                              const plan = typeof selectedCommunity.plan_details === 'string' ? JSON.parse(selectedCommunity.plan_details) : selectedCommunity.plan_details;
                              purpose = plan?.tagline || plan?.description || purpose;
                            } catch {}
                          }
                          return purpose || 'Descrição da comunidade';
                        })()}
                      </VuiTypography>
                    </VuiBox>
                    <VuiBox flex={1} display="flex" flexDirection="row" alignItems="flex-end" gap={1} >
                      <VuiBox
                        variant="gradient"
                        bgColor="viuzzPag"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ borderRadius: "5px", width: "50px", height: "50px" }}
                      >
                        {getPlatformIcon()}
                      </VuiBox>
                      <VuiBox 
                        display="flex" 
                        alignItems="center" 
                        gap={1}
                        sx={{
                          cursor: 'pointer',
                          padding: '0px 12px',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          left: '3px',
                          top: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(252, 101, 8, 0.1)', 
                            transform: 'scale(1.02)',
                            '& .edit-icon': {
                              opacity: 1
                            }
                          }
                        }}
                        onClick={handleOpenEditMembersModal}
                        title="Clique para editar o número de membros"
                      >
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            color: '#FC6508', 
                            fontWeight: 'regular', 
                            fontSize: '50px'
                          }}
                        >
                          {editableMembersData.members}
                        </Typography>
                        <RiEditLine 
                          size="20px" 
                          color="#FC6508" 
                          className="edit-icon"
                          style={{ 
                            opacity: 0.7,
                            transition: 'opacity 0.2s ease'
                          }} 
                        />
                      </VuiBox>
                      <VuiBox>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ color: editableMembersData.isPositive ? '#5ACC93' : '#D7007B', fontSize: "12px" }}>
                            ({editableMembersData.isPositive ? '+' : '-'}{editableMembersData.change})
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: "12px" }}>
                            na última semana
                          </Typography>
                        </VuiBox>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body1" sx={{ color: '#FFFFFF', fontSize: '16px' }}>
                            Membros
                          </Typography>
                        </VuiBox>
                      </VuiBox>
                    </VuiBox>
                  </VuiBox>
                  <VuiBox flex={1} display="flex" justifyContent="flex-end" gap={2} flexDirection="column">
                    <VuiBox flex={1} display="flex" justifyContent="flex-end" gap={2}>
                      <S.StatusBox>
                        <VuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VuiBox sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            bgcolor: '#5ACC93'
                          }}/>
                          <Typography variant="body2" sx={{ color: "#FFFFFF", fontSize: "16px" }}>
                            {getCurrentWeekInfo().text}
                          </Typography>
                        </VuiBox>
                      </S.StatusBox>
                      <VuiBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <CustomButton onClick={handleViewCommunity} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <IoEyeSharp size="18px" />
                          <VuiTypography sx={{ color: '#FFFFFF', fontSize: '16px' }}>
                            Ver comunidade
                          </VuiTypography>
                        </CustomButton>
                      </VuiBox>
                    </VuiBox>
                    <VuiBox flex={1} display="flex" justifyContent="flex-end" gap={2}>
                      <TokenCounter />
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} md={4}>
              <DashboardCard
                icon={calendarIcon}
                title="Progresso do Calendário"
                progressText={`${calendarProgress.completed}/${calendarProgress.total}`}
                progressLabel="dias concluídos"
                buttonText="Acessar calendário"
                onButtonClick={handleNavigateToCalendar}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DashboardCard
                icon={<IoRocketSharp color="#FC6508" size="45px" />}
                title="Módulo Atual"
                progressText="Criador ativo"
                buttonText="Ver Roteiros"
                onButtonClick={handleNavigateToScripts}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DashboardCard
                icon={medalIcon}
                title="Última Conquista"
                progressText="Semana 1 finalizada!"
                buttonText="Ver Medalhas"
                onButtonClick={handleNavigateToMedals}
              />
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} md={7}>
              <Card sx={{ height: "100%", py: "32px", padding: "25px 22px" }}>
                <S.GraphMainContainer container>
                  <Grid container flexDirection='column'>
                    <Grid container display='flex' justifyContent='space-between'>
                      <Grid item xs={6} md={6} xl={3}>
                        <p className='cardTitle'>Membros</p>
                        <Grid container gap='2px' sx={{ color: getMonthlyVariation().isPositive ? '#65EFAD' : '#D7007B', alignItems: 'center' }}>
                          <p>
                            {getMonthlyVariation().text}
                          </p>
                          <p style={{ color: '#FFFFFF', fontSize: '14px', opacity: '0.5' }}>no último mês</p>
                        </Grid>
                      </Grid>
                      <S.BoardsGrid container item xs={6} md={6} xl={9}>
                        <S.SocialGrid className='instagram' px={3}>
                          <S.SocialNameGrid sx={{ fontSize: '14px' }}>
                            <p>Membros Ativos</p>
                          </S.SocialNameGrid>
                          <Grid container justifyContent='center' alignItems='center' sx={{ fontSize: '18px' }}>
                            <p>{Math.floor((editableMembersData.members || 0) * 0.40)}</p>
                            {getBaselineVariation().isPositive ? (
                              <IoArrowUp color="#65EFAD" size="18px" />
                            ) : (
                              <IoArrowDown color="#D7007B" size="18px" />
                            )}
                          </Grid>
                        </S.SocialGrid>
                      </S.BoardsGrid>
                    </Grid>
                    <Grid sx={{ minHeight: "310px", marginTop: "30px" }}>
                      <LineChart 
                        lineChartData={chartData} 
                        lineChartOptions={chartOptions} 
                      />
                    </Grid>
                  </Grid>
                </S.GraphMainContainer>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  Ranking de Membros
                </VuiTypography>
                {memoizedInfluencerList?.length > 0 && memoizedInfluencerList.map((influencer, index) => (
                  <RankingItem key={`ranking-${index}`} influencer={influencer} index={index} />
                ))}
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
      </VuiBox>
      {communities.length === 0 && (
        <Box sx={{ "& > :not(style)": { m: 1 } }}>
          <S.ModalFab color="primary" onClick={handleCreateCommunity}>
            <RiAddFill size="30px" />
          </S.ModalFab>
        </Box>
      )}

      <Modal open={openEditMembersModal} onClose={handleCloseEditMembersModal}>
        <S.MembersEditModal>
          <S.MembersEditTitle variant="h6" component="h2">
            Editar Informações de Membros
          </S.MembersEditTitle>
          
          <S.MembersEditForm>
            <S.MembersEditField>
              <S.MembersEditLabel variant="body2">
                Número de Membros
              </S.MembersEditLabel>
              <S.MembersEditTextField
                fullWidth
                type="number"
                value={editableMembersData.members}
                onChange={(e) => handleMembersDataChange('members', parseInt(e.target.value) || 0)}
              />
            </S.MembersEditField>

            <S.MembersInfoBox>
              <S.MembersInfoTitle variant="body2">
                Variação Calculada Automaticamente
              </S.MembersInfoTitle>
              <S.MembersInfoText>
                Baseline: <strong>{editableMembersData.previousMembers || 'Carregando...'}</strong> (atualiza todo domingo)<br/>
                Quantidade atual: <strong>{editableMembersData.members || 0}</strong><br/>
                Variação: <strong style={{ color: (editableMembersData.members - editableMembersData.previousMembers) >= 0 ? '#5ACC93' : '#D7007B' }}>
                  {(editableMembersData.members - editableMembersData.previousMembers) >= 0 ? '+' : ''}
                  {editableMembersData.members - editableMembersData.previousMembers}
                </strong>
              </S.MembersInfoText>
              <S.MembersInfoTip>
                💡 {getNextBaselineUpdateInfo()}
              </S.MembersInfoTip>
            </S.MembersInfoBox>
          </S.MembersEditForm>

          <S.MembersModalActions>
            <S.MembersCancelButton
              variant="outlined"
              onClick={handleCloseEditMembersModal}
            >
              Cancelar
            </S.MembersCancelButton>
            <S.MembersSaveButton
              onClick={handleSaveMembersData}
            >
              Salvar
            </S.MembersSaveButton>
          </S.MembersModalActions>
        </S.MembersEditModal>
      </Modal>
      <T.ToastContainer />
    </DashboardLayout>
  );
};

export default CommunityDashboard;